import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { SupervisedUserCircleOutlined } from '@mui/icons-material/';
import { Box, CircularProgress, Typography, Stack, Tooltip } from '@mui/material';
import { FilterCategory, RecordType, URLPaths } from 'models/enums';
import useSegmentPreviewCount from 'hooks/mutations/useSegmentPreviewCount';
import useCreateSegment, { SegmentPostBody } from 'hooks/mutations/useCreateSegment';
import QueryKeys from 'hooks/queries/keys';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSends from 'hooks/queries/useSends';
import Button from 'domains/core/components/Button';
import PageLayout from 'domains/core/components/PageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import TextField from 'domains/core/components/TextField';
import { SegmentDefinition, SegmentFilterCategorySpecificField, SegmentFilterState } from 'domains/segments/types';
import FilterGroup from './FilterGroup';
import PreviewCount from 'domains/segments/pages/SegmentCreatePage/PreviewCount';
import { getDemographicFilters, getEngagementNameToValuesRef } from 'domains/segments/utils';
import {
    isNestedSegmentFiltersValid,
    getNestedSegmentDefinition,
    buildTreeFromDefinition,
    findFirstEngagementName,
} from './utils';
import AutocompleteInput from 'domains/segments/pages/SegmentCreatePage/Filter/FilterValueInput/AutocompleteInput';
import { Value, getOptionValues } from 'domains/core/components/Select/Select';
import useQueryParams from 'hooks/useQueryParams';
import { FilterGroupNode } from './types';
import {
    addNodeAtPath,
    removeNodeAtPath,
    updateFilterAtPath,
    updateMatchTypeAtPath,
    flattenFilters,
    filterTree,
    countFilters,
    createFilterLeafNode,
    createGroupNode,
    pruneEmptyGroups,
} from './treeUtils';

type DateTimeErrors = { [key: string]: boolean };

type StringOption = Omit<Value, 'label'> & {
    label: string;
};

type SegmentCreateFormInputs = {
    // Typed as `any` to prevent react-hook-form's deep path resolution
    // from hitting circular types in the recursive FilterGroupNode.
    // The actual value is always FilterGroupNode.
    rootGroup: any;
    recordTypes: StringOption[];
    segmentName: string;
};

const allContactsRecordType = {
    label: RecordType.ALL_CONTACTS,
    value: RecordType.ALL_CONTACTS,
};
const autocompleteInputSX = {
    sxAutocomplete: { width: '24rem' },
    sxChip: { textTransform: 'capitalize' },
    sxOption: { textTransform: 'capitalize' },
};

const getHasAllContacts = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.some(({ value }) => value === RecordType.ALL_CONTACTS);
const removeOtherOptions = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.filter(({ value }) => value === RecordType.ALL_CONTACTS);
const removeAllContactsOption = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.filter(({ value }) => value !== RecordType.ALL_CONTACTS);

export const MAX_FILTER_LENGTH = 25;

/**
 * Creates audiences by segmenting the contacts based on nested filters.
 * This is the nested/grouped version of the segment create page,
 * gated behind the 'audience-builder-nested' feature flag.
 *
 * @returns The React node created by this component.
 */
const NestedSegmentCreatePage = () => {
    const [datetimeErrors, setDatetimeErrors] = useState<DateTimeErrors>({});
    const [isAudiencePreviewed, setIsAudiencePreviewed] = useState(false);
    const [valuesRef, setValuesRef] = useState('');
    const [recordTypeInputValue, setRecordTypeInputValue] = useState('');

    // Counter for generating numeric filter IDs (SegmentFilterState.id)
    const nextFilterIdRef = useRef(0);
    // Guard to ensure the sends fetch is triggered at most once
    const hasFetchedSendsRef = useRef(false);
    // Guard to ensure the copy effect only runs once (same issue as SegmentCreatePage: each
    // sendById resolving triggers setValuesRef → new sends → copy effect rebuilds with wrong
    // sends list → autocomplete values cleared).
    const copyEffectRunRef = useRef(false);
    const getNextFilterId = () => {
        const id = nextFilterIdRef.current;
        nextFilterIdRef.current += 1;
        return id;
    };

    const { data: segmentFilters, isSuccess: isSegmentFiltersSuccess } = useSegmentFilters({
        version: 3,
    });

    const {
        control,
        formState: { isValid },
        getValues,
        setValue,
        watch,
    } = useForm<SegmentCreateFormInputs>({
        mode: 'onTouched',
        defaultValues: {
            rootGroup: {
                id: 'root',
                matchType: 'AND',
                children: [],
            },
            recordTypes: [],
            segmentName: '',
        },
    });

    // Watch reactive values that affect rendering
    const rootGroup = watch('rootGroup');
    const watchedRecordTypes = watch('recordTypes');
    const stateRecordTypeOptions: StringOption[] = useMemo(() => watchedRecordTypes ?? [], [watchedRecordTypes]);
    const isRecordTypesEmpty = stateRecordTypeOptions.length === 0;

    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;

    const segmentRecordTypeOptions: Value[] = useMemo(
        () => [
            allContactsRecordType,
            ...(segmentRecordTypes?.map(({ recordType }) => ({
                label: recordType,
                value: recordType,
            })) ?? []),
        ],
        [segmentRecordTypes]
    );

    const recordTypes = useMemo(() => getOptionValues(stateRecordTypeOptions), [stateRecordTypeOptions]);

    const hasAllContacts = useMemo(() => isRecordTypesEmpty || getHasAllContacts(stateRecordTypeOptions), [
        isRecordTypesEmpty,
        stateRecordTypeOptions,
    ]);

    const demographicFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const result = getDemographicFilters({ hasAllContacts, recordTypes, segmentRecordTypes });

        return result;
    }, [segmentFilters, hasAllContacts, recordTypes, segmentRecordTypes]);

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        // RecordTypes[0] represents engagement filters because there's a single item and its recordType value is "" from the API
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const eventRecordTypeFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        return segmentFilters.contactEvent.recordTypes;
    }, [segmentFilters]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    const { data: sends } = useSends({
        path: valuesRef,
        reactQueryOptions: { enabled: Boolean(valuesRef) },
    });

    const {
        mutate: getAudienceCount,
        isError: isAudienceCountError,
        isLoading: isAudienceCountLoading,
        error: audienceCountError,
        data: audienceCount,
    } = useSegmentPreviewCount();

    const datetimeError = Object.values(datetimeErrors).includes(true);

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors({ ...datetimeErrors, [filterId]: error });

    const history = useHistory();
    const queryClient = useQueryClient();
    const query = useQueryParams();
    const parentFolderId = Number(query.get('parentFolderId'));

    const onSuccess = () => {
        queryClient.invalidateQueries({
            // Updating the audiences to get the last segment added
            predicate: (query: any) => {
                const firstQueryKey: string = query.queryKey[0];
                const invalidate = firstQueryKey === QueryKeys.SEGMENTS[0];
                return invalidate;
            },
        });
        const segmentsPath = parentFolderId ? `${URLPaths.SEGMENTS_FOLDER}/${parentFolderId}` : URLPaths.SEGMENTS;
        history.push(segmentsPath);
    };

    const { mutate: saveSegment, error: createSegmentError, isError: isCreateSegmentError } = useCreateSegment({
        onSuccess,
    });

    // ─── Tree mutation handlers ─────────────────────────────────────────

    const handleUpdateFilter = useCallback(
        (
            path: number[],
            filterData: SegmentFilterState<SegmentFilterCategorySpecificField>,
            resetPreview: boolean = true
        ) => {
            const currentTree = getValues('rootGroup');
            setValue('rootGroup', updateFilterAtPath(currentTree, path, filterData));

            if (resetPreview) {
                setIsAudiencePreviewed(false);
            }

            if (filterData.category === FilterCategory.CONTACT_ACTIVITY && filterData.name) {
                setValuesRef(engagementNameToValuesRef[filterData.name]);
            }
        },
        [getValues, setValue, engagementNameToValuesRef]
    );

    const handleRemoveNode = useCallback(
        (path: number[]) => {
            const currentTree = getValues('rootGroup');
            const afterRemove = removeNodeAtPath(currentTree, path);
            // Prune any groups that became empty after the removal
            setValue('rootGroup', pruneEmptyGroups(afterRemove));
            setIsAudiencePreviewed(false);
        },
        [getValues, setValue]
    );

    const handleUpdateMatchType = useCallback(
        (path: number[], matchType: 'AND' | 'OR') => {
            const currentTree = getValues('rootGroup');
            setValue('rootGroup', updateMatchTypeAtPath(currentTree, path, matchType));
            setIsAudiencePreviewed(false);
        },
        [getValues, setValue]
    );

    const handleAddFilter = useCallback(
        (path: number[]) => {
            const currentTree = getValues('rootGroup');
            const allFilters = flattenFilters(currentTree);
            const lastFilter = allFilters[allFilters.length - 1];
            const lastFilterCategory = lastFilter?.category;

            const newLeaf = createFilterLeafNode({
                id: getNextFilterId(),
                category: lastFilterCategory,
                categorySpecificFields: { value: '' },
                name: '',
                operator: undefined,
            });

            setValue('rootGroup', addNodeAtPath(currentTree, path, newLeaf));
            setIsAudiencePreviewed(false);
        },
        [getValues, setValue]
    );

    const isRootInInitialState = (tree: FilterGroupNode): boolean => {
        if (tree.children.length !== 1) return false;
        const child = tree.children[0];
        if (child.type !== 'filter') return false;
        return !child.filter.name && !child.filter.operator;
    };

    const handleAddGroup = useCallback(
        (path: number[]) => {
            const currentTree = getValues('rootGroup');

            const defaultLeaf = createFilterLeafNode({
                id: getNextFilterId(),
                category: undefined,
                categorySpecificFields: { value: '' },
                name: '',
                operator: undefined,
            });

            const newGroup = createGroupNode('AND', [defaultLeaf]);

            if (path.length === 0 && isRootInInitialState(currentTree)) {
                setValue('rootGroup', { ...currentTree, children: [newGroup] });
            } else {
                setValue('rootGroup', addNodeAtPath(currentTree, path, newGroup));
            }

            setIsAudiencePreviewed(false);
        },
        [getValues, setValue]
    );

    // ─── Segment conversion & actions ───────────────────────────────────

    const getSegment = () => {
        const currentTree = getValues('rootGroup');

        return getNestedSegmentDefinition({
            rootGroup: currentTree,
            hasAllContacts,
            recordTypes,
        });
    };

    const previewAudienceCount = () => {
        const segmentDefinition = getSegment();

        getAudienceCount(segmentDefinition);
        setIsAudiencePreviewed(true);
    };

    const saveAudience = async () => {
        const segmentDefinition = getSegment();
        const segmentName = getValues('segmentName');

        const body: SegmentPostBody = {
            name: segmentName,
            nestingAllowed: true,
            segmentDefinition,
            ...(parentFolderId ? { parentFolderId } : {}),
        };
        saveSegment(body);
    };

    // ─── Record type change handling ────────────────────────────────────

    const getNewRecordTypes = (autocompleteOptions: StringOption[]): StringOption[] => {
        const isNewValueAllContacts = getHasAllContacts(autocompleteOptions);
        const isOldValueAllContacts = getHasAllContacts(stateRecordTypeOptions);

        return isNewValueAllContacts
            ? isOldValueAllContacts
                ? removeAllContactsOption(autocompleteOptions)
                : removeOtherOptions(autocompleteOptions)
            : autocompleteOptions;
    };

    const getNewFiltersByRecordTypes = (newRecordTypes: StringOption[]): FilterGroupNode => {
        const newRecordTypeValues = getOptionValues(newRecordTypes);
        const newDemographicFilters = getDemographicFilters({
            hasAllContacts: getHasAllContacts(newRecordTypes),
            recordTypes: newRecordTypeValues,
            segmentRecordTypes,
        });

        const filterNameSet = new Set(newDemographicFilters.map((filter) => filter.name));

        return filterTree(
            getValues('rootGroup'),
            (filter) => filter.category !== FilterCategory.CONTACT_DETAILS || filterNameSet.has(filter.name)
        );
    };

    const changeRecordTypesAndFilters = (autocompleteOptions: StringOption[]) => {
        const newRecordTypes = getNewRecordTypes(autocompleteOptions);

        setValue('recordTypes', newRecordTypes);
        setRecordTypeInputValue('');

        const newTree = getNewFiltersByRecordTypes(newRecordTypes);
        setValue('rootGroup', newTree);
    };

    // ─── Copy / Duplicate audience flow ─────────────────────────────────

    const location = useLocation();
    const state = location?.state as { segmentDefinition: SegmentDefinition };
    const { contactRecordType } = state?.segmentDefinition || {};

    const isFiltersEmpty = rootGroup.children.length === 0;
    const isSegmentNew = !location.state;

    useEffect(() => {
        // If this is not a copy and FF is enabled, then add 'All Contacts' record type.
        if (isSegmentNew && hasAllContacts) {
            setValue('recordTypes', [allContactsRecordType]);
        }
    }, [hasAllContacts, isSegmentNew, setValue]);

    useEffect(() => {
        // If this is not a copy, then add a filter.
        if (isSegmentNew && isFiltersEmpty) {
            handleAddFilter([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        /*
        If there are engagement filters anywhere in the definition tree (including subsegments),
        set the valuesRef once to trigger the sends request.
        Re-runs when engagementNameToValuesRef is populated (after segmentFilters loads).
        The ref guard ensures setValuesRef is called at most once.
        */
        if (hasFetchedSendsRef.current || !state?.segmentDefinition) return;

        const firstEngagementName = findFirstEngagementName(state.segmentDefinition);

        if (firstEngagementName && engagementNameToValuesRef[firstEngagementName]) {
            setValuesRef(engagementNameToValuesRef[firstEngagementName]);
            hasFetchedSendsRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engagementNameToValuesRef]);

    useEffect(() => {
        if (!location.state) return;

        // If engagement filters exist in the definition, wait until sends have loaded
        // so the tree is built once with complete data (avoids unnecessary re-renders).
        const hasEngagement = Boolean(findFirstEngagementName(state.segmentDefinition));
        if (hasEngagement && !sends) return;

        // Only run once: each sendById resolving triggers setValuesRef → new sends load.
        // Without this guard, the tree rebuilds with the new sends list and any send not
        // present in it gets autocompleteValue = null (values cleared).
        if (copyEffectRunRef.current) return;
        copyEffectRunRef.current = true;

        const tree = buildTreeFromDefinition(state.segmentDefinition, sends, getNextFilterId);

        const recordTypeValues: StringOption[] = contactRecordType?.map((value) => ({ label: value, value }));

        setValue('rootGroup', { ...tree, id: 'root' });
        setValue('recordTypes', recordTypeValues ?? [allContactsRecordType]);

        nextFilterIdRef.current = countFilters(tree);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactRecordType, location.state, sends, setValue]);

    // ─── Validation ─────────────────────────────────────────────────────

    const hasInvalidFilters = !isNestedSegmentFiltersValid(rootGroup, isRecordTypesEmpty);
    const totalFilterCount = countFilters(rootGroup);
    const isMaxFilterLimit = totalFilterCount >= MAX_FILTER_LENGTH;
    const isPreviewDisabled = datetimeError || hasInvalidFilters || isAudienceCountLoading;
    const isSaveDisabled = datetimeError || hasInvalidFilters || !isValid || isRecordTypesEmpty;

    return (
        <PageLayout
            header={
                <Tooltip
                    title="contacts grouped by a set of filter criteria so you can send targeted messages"
                    aria-label="contacts grouped by a set of filter criteria so you can send targeted messages"
                >
                    <Typography variant="h4">audiences</Typography>
                </Tooltip>
            }
            headerIcon={<SupervisedUserCircleOutlined fontSize="inherit" />}
            headerAction={
                <Stack direction="row" columnGap="1rem">
                    <Button
                        onClick={() => {
                            const segmentsPath = parentFolderId
                                ? `${URLPaths.SEGMENTS_FOLDER}/${parentFolderId}`
                                : URLPaths.SEGMENTS;
                            history.push(segmentsPath);
                        }}
                    >
                        Cancel
                    </Button>
                    <Tooltip title={isSaveDisabled ? 'Fill all the fields to enable saving' : ''}>
                        <span>
                            <Button
                                disabled={isSaveDisabled}
                                endIcon={<SupervisedUserCircleOutlined />}
                                onClick={saveAudience}
                                variant="contained"
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            }
        >
            <Stack pb={4}>
                {isCreateSegmentError && <ErrorSnackbar errorMessage={createSegmentError.message} />}
                <Stack direction="row" alignItems="center" columnGap={2}>
                    <Controller
                        name="segmentName"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: 'This field is required',
                            minLength: { value: 3, message: 'name must have at least 3 characters' },
                            maxLength: { value: 255, message: 'name must be under 255 characters' },
                        }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error, invalid } }) => (
                            <TextField
                                required
                                label="Audience name"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={invalid}
                                helperText={invalid ? error.message : null}
                                width="24rem"
                            />
                        )}
                    />
                    <Tooltip
                        title="Preview the number of current contacts that meet the applied filters"
                        aria-label="preview the number of current contacts that meet the applied filters"
                    >
                        <Box>
                            <Button
                                disabled={isPreviewDisabled}
                                variant="outlined"
                                onClick={previewAudienceCount}
                                size="medium"
                            >
                                Preview
                            </Button>
                        </Box>
                    </Tooltip>
                    <Stack direction="row" justifyContent="flex-start">
                        <PreviewCount
                            isAudiencePreviewed={isAudiencePreviewed}
                            data={audienceCount}
                            error={audienceCountError}
                            isError={isAudienceCountError}
                            loading={isAudienceCountLoading}
                        />
                    </Stack>
                </Stack>
                <Stack mt={4} mb={2}>
                    <Typography variant="h6">create audience</Typography>
                    <Typography variant="body2" marginTop=".5rem">
                        Start by selecting the contact types you want to target. Your filters will be applied to these
                        contacts only.
                    </Typography>
                </Stack>

                {isSegmentFiltersSuccess && (
                    <Box mt={1} mb={3}>
                        <Controller
                            control={control}
                            name="recordTypes"
                            render={({ field: { value }, fieldState: { error, invalid } }) => (
                                <AutocompleteInput
                                    autocompleteValue={value}
                                    autocompleteInputValue={recordTypeInputValue}
                                    error={invalid}
                                    helperText={invalid ? error.message : null}
                                    multiple
                                    onChange={changeRecordTypesAndFilters}
                                    onInputChange={setRecordTypeInputValue}
                                    options={segmentRecordTypeOptions}
                                    placeholderName="Contact Type"
                                    styles={autocompleteInputSX}
                                    type="string"
                                />
                            )}
                            rules={{
                                required: 'Select contact type',
                            }}
                        />
                    </Box>
                )}
                <Stack mt={1}>
                    <Typography variant="h6">filters</Typography>
                    <Typography variant="body2" marginTop=".5rem">
                        Add conditions to narrow down and define your audience.
                    </Typography>
                </Stack>
                {isSegmentFiltersSuccess &&
                    stateRecordTypeOptions.length > 0 &&
                    (!isSegmentNew && isFiltersEmpty ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <FilterGroup
                            group={rootGroup}
                            path={[]}
                            depth={0}
                            demographicFilters={demographicFilters}
                            engagementFilters={engagementFilters}
                            eventRecordTypeFilters={eventRecordTypeFilters}
                            sends={sends}
                            handleDatetimeError={handleDatetimeError}
                            isMaxFilterLimit={isMaxFilterLimit}
                            onAddFilter={handleAddFilter}
                            onAddGroup={handleAddGroup}
                            onUpdateFilter={handleUpdateFilter}
                            onRemoveNode={handleRemoveNode}
                            onUpdateMatchType={handleUpdateMatchType}
                        />
                    ))}
            </Stack>
        </PageLayout>
    );
};

export default NestedSegmentCreatePage;
